import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";

const STORAGE_KEY = "killavox_cookie_consent_v1";

function updateGtagConsent(consent) {
  if (typeof window === "undefined") return;
  const w = window;
  w.dataLayer = w.dataLayer || [];
  function gtag() { w.dataLayer.push(arguments); }
  w.gtag = w.gtag || gtag;
  const analytics = consent.analytics ? "granted" : "denied";
  const marketing = consent.marketing ? "granted" : "denied";
  const personalization = consent.personalization ? "granted" : "denied";
  w.gtag("consent", "update", {
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    analytics_storage: analytics,
    personalization_storage: personalization,
    functionality_storage: "granted",
    security_storage: "granted",
  });
}

export default function CookieBanner() {
  const { lang, t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    personalization: false,
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setVisible(true);
      } else {
        const saved = JSON.parse(raw);
        updateGtagConsent(saved);
      }
    } catch (_) {
      setVisible(true);
    }
  }, []);

  const persist = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(choice));
    } catch (_) {
      /* ignore */
    }
    updateGtagConsent(choice);
    setVisible(false);
    setExpanded(false);
  };

  const acceptAll = () => persist({ necessary: true, analytics: true, marketing: true, personalization: true });
  const rejectAll = () => persist({ necessary: true, analytics: false, marketing: false, personalization: false });
  const savePrefs = () => persist(prefs);

  if (!visible) return null;

  return (
    <div className="kv-cookie-banner" role="dialog" aria-label={t("cookieBanner.title")} data-testid="cookie-banner">
      {!expanded ? (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-start", justifyContent: "space-between" }}>
            <div style={{ maxWidth: 460 }}>
              <div className="kv-h3" style={{ marginBottom: "0.5rem", fontSize: "1.05rem" }}>
                {t("cookieBanner.title")}
              </div>
              <div className="kv-body" style={{ fontSize: "0.9rem" }}>
                {t("cookieBanner.body")}{" "}
                <Link to={`/${lang}/cookies`} className="kv-link" data-testid="cookie-banner-readmore">
                  {t("cookieBanner.readMore")}
                </Link>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              <button className="kv-btn-ghost" onClick={rejectAll} data-testid="cookie-reject" type="button">
                {t("cookieBanner.reject")}
              </button>
              <button className="kv-btn-ghost" onClick={() => setExpanded(true)} data-testid="cookie-manage" type="button">
                {t("cookieBanner.manage")}
              </button>
              <button className="kv-btn-primary" onClick={acceptAll} data-testid="cookie-accept" type="button">
                {t("cookieBanner.accept")}
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="kv-h3" style={{ marginBottom: "0.35rem", fontSize: "1.1rem" }}>
            {t("cookieBanner.prefTitle")}
          </div>
          <div className="kv-body" style={{ fontSize: "0.9rem", marginBottom: "1rem" }}>
            {t("cookieBanner.prefBody")}
          </div>
          {[
            { key: "necessary", disabled: true },
            { key: "analytics", disabled: false },
            { key: "marketing", disabled: false },
            { key: "personalization", disabled: false },
          ].map(({ key, disabled }) => (
            <label
              key={key}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "0.75rem",
                padding: "0.6rem 0",
                borderTop: "1px solid var(--kv-gray-2)",
              }}
              data-testid={`cookie-pref-${key}`}
            >
              <input
                type="checkbox"
                checked={prefs[key]}
                disabled={disabled}
                onChange={(e) => setPrefs((p) => ({ ...p, [key]: e.target.checked }))}
                style={{ marginTop: 4 }}
              />
              <div>
                <div style={{ fontWeight: 700, color: "var(--kv-navy)" }}>{t(`cookieBanner.${key}`)}</div>
                <div style={{ fontSize: "0.85rem", color: "var(--kv-muted)" }}>{t(`cookieBanner.${key}Body`)}</div>
              </div>
            </label>
          ))}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "1rem", justifyContent: "flex-end" }}>
            <button className="kv-btn-ghost" onClick={() => setExpanded(false)} data-testid="cookie-cancel" type="button">
              {t("cookieBanner.cancel")}
            </button>
            <button className="kv-btn-primary" onClick={savePrefs} data-testid="cookie-save" type="button">
              {t("cookieBanner.save")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
